/**
 * Purge Utilities
 *
 * Helper functions for the FI Data Purge feature
 */

import type { PurgeDataRecord, PurgeTableRow, PurgeAggregationLevel } from '../types/purge';

/**
 * Format a date string or Date object to YYYY-MM-DD
 * Uses local timezone to avoid date shifting issues
 */
export const formatDateToISO = (date: Date | string): string => {
  if (typeof date === 'string') {
    return date.split('T')[0]; // Handle ISO strings
  }
  // Use local date components to avoid UTC conversion issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a period string based on aggregation level
 * - Daily: "2024-11-01" -> "Nov 01, 2024"
 * - Monthly: "2024-11" -> "November 2024"
 */
export const formatPeriodDisplay = (period: string, level: PurgeAggregationLevel): string => {
  if (level === 'day') {
    const date = new Date(period);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } else {
    // Monthly format: "2024-11" -> "November 2024"
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  }
};

/**
 * Format a number with commas for display
 * Example: 1234567 -> "1,234,567"
 */
export const formatNumberWithCommas = (num: number): string => {
  return num.toLocaleString('en-US');
};

/**
 * Convert purge data records to table rows with formatted display
 */
export const toTableRows = (
  records: PurgeDataRecord[],
  level: PurgeAggregationLevel
): PurgeTableRow[] => {
  return records.map((record, index) => ({
    ...record,
    _rowId: `${record.period}-${index}`,
    displayPeriod: formatPeriodDisplay(record.period, level),
  }));
};

/**
 * Calculate date range based on quick filter selection
 * Always returns full months (first day of start month, last day of end month)
 */
export const calculateQuickFilterDates = (
  filter: 'last_month' | 'last_3_months' | 'last_6_months'
): { startDate: Date; endDate: Date } => {
  const now = new Date();

  // End date: last day of current month
  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  // Start date: first day of the month X months ago
  let monthsBack = 1;
  switch (filter) {
    case 'last_month':
      monthsBack = 1;
      break;
    case 'last_3_months':
      monthsBack = 3;
      break;
    case 'last_6_months':
      monthsBack = 6;
      break;
  }

  const startDate = new Date(now.getFullYear(), now.getMonth() - monthsBack + 1, 1);

  return { startDate, endDate };
};

/**
 * Sort purge table rows based on sort order
 */
export const sortPurgeRows = (
  rows: PurgeTableRow[],
  sortOrder: 'newest' | 'oldest' | 'highest' | 'lowest'
): PurgeTableRow[] => {
  const sorted = [...rows];

  switch (sortOrder) {
    case 'newest':
      return sorted.sort((a, b) => b.period.localeCompare(a.period));
    case 'oldest':
      return sorted.sort((a, b) => a.period.localeCompare(b.period));
    case 'highest':
      return sorted.sort((a, b) => b.total_count - a.total_count);
    case 'lowest':
      return sorted.sort((a, b) => a.total_count - b.total_count);
    default:
      return sorted;
  }
};

/**
 * Get label for sort order
 */
export const getSortOrderLabel = (sortOrder: string): string => {
  const labels: Record<string, string> = {
    newest: 'Newest First',
    oldest: 'Oldest First',
    highest: 'Highest Count',
    lowest: 'Lowest Count',
  };
  return labels[sortOrder] || sortOrder;
};

/**
 * Get label for quick filter
 */
export const getQuickFilterLabel = (filter: string): string => {
  const labels: Record<string, string> = {
    last_month: 'Last Month',
    last_3_months: 'Last 3 Months',
    last_6_months: 'Last 6 Months',
    custom: 'Custom Range',
  };
  return labels[filter] || filter;
};

/**
 * Get label for aggregation level
 */
export const getAggregationLabel = (level: PurgeAggregationLevel): string => {
  return level === 'day' ? 'Daily' : 'Monthly';
};

/**
 * Validate date range (end date must be after start date)
 */
export const isValidDateRange = (startDate: Date, endDate: Date): boolean => {
  return endDate >= startDate;
};

/**
 * Calculate default date range (last 6 months)
 */
export const getDefaultDateRange = (): { startDate: Date; endDate: Date } => {
  return calculateQuickFilterDates('last_6_months');
};

/**
 * Check if a period (month) is completed - before current month
 * @param period - Period string in format "YYYY-MM" for monthly
 * @returns true if the period is completed (not current or future)
 */
export const isPeriodCompleted = (period: string): boolean => {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return period < currentMonth;
};

/**
 * Get month name from period string "YYYY-MM"
 * @param period - Period string in format "YYYY-MM"
 * @returns Full month name (e.g., "December")
 */
export const getMonthNameFromPeriod = (period: string): string => {
  const [year, month] = period.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long' });
};
